import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingBag, Check, Truck, ShieldCheck, ArrowLeft, ZoomIn, X } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { api, fmtCAD } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { ProductCard } from "@/components/ProductCard";

const Fallback = ({ brand, label }) => (
  <div className="flex flex-col items-center justify-center gap-4 text-center" data-testid="product-image-fallback">
    <img src="/logo.png" alt="DCS Électro" className="h-12 w-auto opacity-90" />
    <span className="font-heading text-2xl font-bold text-brand-navy">{brand}</span>
    <p className="text-gray-500 text-sm uppercase tracking-wider">{label}</p>
  </div>
);

export default function ProductDetail() {
  const { handle } = useParams();
  const { addItem } = useCart();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [sizeIdx, setSizeIdx] = useState(2);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false); setActive(0); window.scrollTo(0, 0);
    api.get(`/products/${handle}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [handle]);

  if (notFound) return <div className="mx-auto max-w-7xl px-4 py-32 text-center"><Link to="/boutique" className="text-brand-red font-semibold">← {t("nav.shop")}</Link></div>;
  if (!data) return <div className="mx-auto max-w-7xl px-4 py-32 text-center text-gray-400">…</div>;

  const p = data.product;
  const onSale = p.compare_at && p.compare_at > p.price;
  const gallery = (Array.isArray(p.images) && p.images.length ? p.images : (p.image ? [p.image] : [])).filter(Boolean);
  const mainImg = gallery[active] || gallery[0];
  const specs = Array.isArray(p.specs) ? p.specs.filter((s) => s && (s.label || s.name) && s.value).map((s) => ({ label: s.label || s.name, value: s.value })) : [];
  const descHtml = p.description ? DOMPurify.sanitize(p.description) : "";

  const DescBlock = () => descHtml ? (
    <div className="mt-8 border-t border-gray-200 pt-6" data-testid="product-description">
      <h3 className="font-semibold mb-3 text-lg">{t("product.description")}</h3>
      <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mt-1" dangerouslySetInnerHTML={{ __html: descHtml }} />
    </div>
  ) : null;

  const SpecsBlock = () => specs.length ? (
    <div className="mt-8 border-t border-gray-200 pt-6" data-testid="product-specs">
      <h3 className="font-semibold mb-3 text-lg">{t("product.specs")}</h3>
      <table className="w-full text-sm">
        <tbody>
          {specs.map((s, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="py-2 pr-4 text-gray-500 w-1/2 align-top">{s.label}</td>
              <td className="py-2 font-medium text-gray-800">{s.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : null;

  const Finance = () => (
    <div className="mt-6 border border-gray-200 bg-gray-50 p-4 flex items-center justify-between gap-4" data-testid="finance-widget">
      <div>
        <p className="font-semibold text-brand-navy text-sm">{t("product.finance")}</p>
        <p className="text-xs text-gray-500 mt-0.5">{t("product.financeVia")}</p>
      </div>
      <a href="https://apply.ifinancecanada.com/23386" target="_blank" rel="noopener noreferrer"
        className="animate-pulse shrink-0 inline-flex items-center gap-1.5 bg-brand-red text-white font-bold text-sm px-4 py-2.5 rounded-full shadow hover:bg-[#b81c14]"
        data-testid="finance-ifinance-link">iFinance Canada</a>
    </div>
  );

  const MattressInfo = () => (p.thickness || p.firmness) ? (
    <div className="mt-6 grid grid-cols-2 gap-3" data-testid="mattress-info">
      {p.thickness && <div className="border-2 border-brand-red/20 bg-brand-red/5 p-3 text-center"><span className="block text-[11px] uppercase tracking-wider text-gray-500">{t("product.thickness")}</span><span className="block font-heading text-xl font-extrabold text-brand-navy mt-1">{p.thickness}</span></div>}
      {p.firmness && <div className="border-2 border-brand-red/20 bg-brand-red/5 p-3 text-center"><span className="block text-[11px] uppercase tracking-wider text-gray-500">{t("product.firmness")}</span><span className="block font-heading text-xl font-extrabold text-brand-navy mt-1">{p.firmness}</span></div>}
    </div>
  ) : null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link to="/boutique" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand-red mb-6"><ArrowLeft size={15} /> {t("nav.shop")}</Link>
      <div className="grid md:grid-cols-2 gap-12">
        {/* GALLERY */}
        <div>
          <div className="relative border border-gray-200 bg-gray-50 aspect-square flex items-center justify-center p-10">
            {mainImg
              ? <>
                  <img src={mainImg} alt={p.title} className="max-h-full max-w-full object-contain cursor-zoom-in" data-testid="product-image" onClick={() => setZoom(true)} />
                  <button onClick={() => setZoom(true)} className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 hover:bg-white text-brand-navy text-xs font-semibold px-3 py-2 shadow rounded-full transition-colors" data-testid="product-zoom-btn" aria-label="Agrandir">
                    <ZoomIn size={15} /> {t("product.zoom")}
                  </button>
                </>
              : <Fallback brand={p.brand} label={p.category_label} />}
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3" data-testid="product-gallery-thumbs">
              {gallery.slice(0, 10).map((img, i) => (
                <button key={i} onClick={() => setActive(i)}
                  className={`border aspect-square bg-white flex items-center justify-center p-2 transition-colors ${active === i ? "border-brand-red" : "border-gray-200 hover:border-gray-400"}`}
                  data-testid={`gallery-thumb-${i}`}>
                  <img src={img} alt={`${p.title} ${i + 1}`} className="max-h-full max-w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div className="md:sticky md:top-28 self-start">
          <span className="text-xs uppercase tracking-widest text-brand-red font-semibold">{p.brand} · {p.category_label}</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 leading-tight" data-testid="product-title">{p.title}</h1>
          {p.sku && <p className="text-sm text-gray-400 mt-2">{t("product.ref")} : {p.sku}</p>}
          {p.price_on_request ? (
            <div className="mt-6">
              <span className="text-3xl font-bold text-brand-navy" data-testid="product-price">{t("product.onRequest")}</span>
              <div className="mt-6">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-semibold px-8 py-4 hover:bg-black transition-colors" data-testid="pdp-ask-price">
                  {t("product.askPrice")}
                </Link>
              </div>
              <Finance />
              <DescBlock />
              <SpecsBlock />
            </div>
          ) : (
          <>
          <div className="mt-6 flex items-center gap-3">
            <span className="text-4xl font-bold" data-testid="product-price">{fmtCAD(p.sizes?.length ? p.sizes[Math.min(sizeIdx, p.sizes.length - 1)].price : p.price)}</span>
            {onSale && <span className="text-xl text-gray-400 line-through">{fmtCAD(p.compare_at)}</span>}
          </div>
          <div className={`mt-4 inline-flex items-center gap-2 text-sm font-medium ${p.in_stock ? "text-green-600" : "text-gray-500"}`}>
            <Check size={16} /> {p.in_stock ? t("product.inStock") : t("product.onOrder")}
          </div>
          {p.used && p.condition && (
            <span className="mt-3 ml-3 inline-block bg-brand-navy/10 text-brand-navy text-xs font-semibold px-2.5 py-1 rounded-full" data-testid="used-condition">{p.condition}</span>
          )}
          {p.used && p.quantity != null && (
            <span className="mt-3 ml-2 inline-block bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full" data-testid="used-quantity">{t("used.qty")} : {p.quantity}</span>
          )}

          <MattressInfo />

          {p.sizes?.length > 0 && (
            <div className="mt-6" data-testid="size-selector">
              <p className="text-sm font-semibold mb-2">{t("product.chooseSize")}</p>
              <div className="grid grid-cols-2 gap-2">
                {p.sizes.map((s, i) => (
                  <button key={i} onClick={() => setSizeIdx(i)}
                    className={`text-left border px-3 py-2 transition-colors ${sizeIdx === i ? "border-brand-red bg-brand-red/5" : "border-gray-300 hover:border-gray-400"}`}
                    data-testid={`size-option-${i}`}>
                    <span className="font-semibold block text-sm">{s.name}</span>
                    <span className="text-xs text-gray-500">{s.dimensions} · {fmtCAD(s.price)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-gray-300">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 text-lg" data-testid="qty-minus">−</button>
              <span className="px-4 min-w-[3rem] text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3 text-lg" data-testid="qty-plus">+</button>
            </div>
            <button onClick={() => { const sz = p.sizes?.length ? p.sizes[Math.min(sizeIdx, p.sizes.length - 1)] : null; const item = sz ? { ...p, title: `${p.title} — ${sz.name}`, price: sz.price, id: `${p.id}-s${sizeIdx}` } : p; addItem(item, qty); toast.success(t("product.added"), { description: item.title }); }}
              disabled={p.used && p.status === "sold"}
              className="flex-1 flex items-center justify-center gap-2 bg-brand-red text-white font-semibold py-4 hover:bg-[#b81c14] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" data-testid="pdp-add-to-cart">
              <ShoppingBag size={18} /> {p.used && p.status === "sold" ? t("used.sold") : t("product.addToCart")}
            </button>
          </div>

          <p className="mt-4 flex items-center gap-2 text-xs text-gray-500" data-testid="pdp-delivery-note">
            <Truck size={14} className="text-brand-red shrink-0" /> {t("product.delivery")}
          </p>

          <Finance />

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 text-sm text-gray-600 border border-gray-200 p-4"><Truck size={20} className="text-brand-red" /> {t("home.f2t")}</div>
            <div className="flex items-center gap-3 text-sm text-gray-600 border border-gray-200 p-4"><ShieldCheck size={20} className="text-brand-red" /> {t("home.f1t")}</div>
          </div>

          <DescBlock />
          <SpecsBlock />
          </>
          )}
        </div>
      </div>

      {zoom && mainImg && (
        <div className="fixed inset-0 z-[110] bg-black/90 flex items-center justify-center p-4" onClick={() => setZoom(false)} data-testid="image-zoom-overlay">
          <img src={mainImg} alt={p.title} className="max-h-[92vh] max-w-[92vw] object-contain" onClick={(e) => e.stopPropagation()} />
          {gallery.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2" onClick={(e) => e.stopPropagation()}>
              {gallery.slice(0, 10).map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`h-14 w-14 border-2 bg-white flex items-center justify-center p-1 ${active === i ? "border-brand-red" : "border-white/40"}`}>
                  <img src={img} alt="" className="max-h-full max-w-full object-contain" />
                </button>
              ))}
            </div>
          )}
          <button onClick={() => setZoom(false)} className="absolute top-4 right-4 h-11 w-11 flex items-center justify-center bg-white/15 hover:bg-white/25 text-white rounded-full" data-testid="image-zoom-close" aria-label="Fermer"><X size={24} /></button>
        </div>
      )}

      {data.related?.length > 0 && (
        <section className="mt-24">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight mb-8">{t("product.similar")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {data.related.map((rp, i) => <ProductCard key={rp.id} product={rp} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
